import React, { useState, PropsWithChildren, useCallback, ReactNode, FC, useMemo, useEffect } from 'react';
import { CSVLink as CSVLinkBase } from 'react-csv';
import {
  Box,
  Button, TableContainer,
  TablePagination,
  Table as MuiTable,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Collapse,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Chip
} from '@material-ui/core';
import { SelectChangeEvent } from '@material-ui/core/Select';
import { ArrowDown, ArrowUp, Filter as FilterIcon } from 'react-feather';
import { makeStyles } from '@material-ui/styles';
import SearchBar from './SearchBar';
import colors from '../colors';
import Fuse from 'fuse.js';
import useTableStore from '../stores/tableState';
import { Checkbox } from '@material-ui/core';
import _ from 'lodash';
import { SortState } from '../types';

interface Column<T> {
  label: string;
  cellFill: keyof T | ((row: T) => ReactNode | React.ComponentType<any>);
  sortable?: boolean;
  searchable?: boolean;
  searchFunction?: (row: T) => string;
  hide?: boolean;
  sortValueGetter?: (row: T) => string | number | Date;
  csv?: boolean;
  csvFill?: (row: T) => string | number;
}

type FilterValue = string | string[];

interface Filter<T> {
  title: string;
  matchingFunction: (row: T) => FilterValue
}


type SortDirection = 'asc' | 'desc' | null
export interface TableState {
  searchTerm: string;
  indexPageNumber: number;
  indexRowsPerPage: number;
  sortColumnId?: string | null;
  sortDirection: SortDirection;
}

interface Props<T> {
  title: string;
  rows: T[],
  search?: boolean;
  columns: Column<T>[];
  handleRowClicked: (row: T) => void;
  loading: boolean;
  filters?: Filter<T>[];
  defaultSelectedFilters?: string[];
  defaultFiltersOn?: boolean;
  rightActions?: ReactNode;
  id: string;
}

interface FilterOption {
  id: string;
  displayLabel: string;
}
interface FilterOptionGroup {
  title: string;
  options: FilterOption[];
}

// react-csv typings are outdated for our setup; cast to a generic component to satisfy TS
const CSVLink = CSVLinkBase as unknown as React.ComponentType<any>;

const Table = <T,>({ rows, columns, handleRowClicked, title, filters, id, defaultSelectedFilters = [], defaultFiltersOn = false, rightActions }: Props<T>) => {
  const defaultSortColumn = columns.find((c) => c.sortable) || columns[0]
  const tableStore = useTableStore().initializeTableState<T>(id, { key: defaultSortColumn.label as keyof T, ascending: true })
  const { setStoreState } = useTableStore()
  const { sortState, searchTerm, indexPageNumber, indexRowsPerPage } = tableStore
  const [displayedRows, setDisplayedRows] = React.useState(rows)
  const [filtersOn, toggleFiltersOn] = useState<boolean>(defaultFiltersOn);
  const [selectedFilterIds, setSelectedFilters] = useState<string[]>(defaultSelectedFilters)
  const filterOptionGroups = useMemo((): FilterOptionGroup[] => {
    if (!filters) {
      return []
    }

    return (
      filters.map((f: Filter<T>) => {
        const optionLabels: string[] = _.uniq(_.flatten(
          rows.map((r: T) => {
            const value = f.matchingFunction(r)
            return Array.isArray(value) ? value : [value]
          })
        ))
        return (
          {
            title: f.title,
            options: optionLabels.sort().map((label) => ({
              id: `${f.title}-${label}`,
              displayLabel: label,
            }))
          }
        )
      })
    )
  }, [filters, rows])
  const fuse = React.useMemo(() => {
    return new Fuse<T>(rows, {
      keys: columns.filter((c) => c.searchable).map((c) => ({ name: c.label, getFn: c.searchFunction })),
      distance: 0,
      threshold: 0,
      useExtendedSearch: true,
    })
  }, [rows, columns]);

  const sort = useCallback((one: T, two: T): number => {
    if (!sortState.key) {
      return 0
    };
    const sortingColumn = columns.find((c) => c.label === sortState.key)
    if (!sortingColumn || !sortingColumn.sortValueGetter) {
      return 0
    }

    const oneValue = sortingColumn.sortValueGetter(one)
    const twoValue = sortingColumn.sortValueGetter(two)

    if (oneValue === twoValue) {
      return 0;
    }

    if (!oneValue) {
      // Empty string shown at bottom of list (After Z)
      return sortState.ascending ? 1 : -1
    }

    if (!twoValue) {
      // Empty string shown at bottom of list (After Z)
      return sortState.ascending ? -1 : 1
    }

    if (oneValue > twoValue) {
      return sortState.ascending ? 1 : -1
    } else if (twoValue > oneValue) {
      return sortState.ascending ? -1 : 1
    } else {
      return 0
    }
  }, [sortState, columns])


  // RUN THE FILTERS
  useEffect(() => {
    let filteredRows = [...rows]

    // Filter ids are in the format `${filterLabel}-${value}`
    // This transforms an array of those filter ids into an object that summarizes
    // the active filters:
    // ["Price-$100", "Price-$200", "Interval-monthly", "Interval-annual", "Auto Renew-On"]
    // Becomes this:
    // {
    //   Price: ["$100", "$200"],
    //   Interval: ["monthly", "annual"],
    //   "Auto Renew": ["On"],
    // }
    const activeFilters = _.chain(selectedFilterIds)
      .groupBy(item => item.split('-')[0])
      .mapValues(groupedItems => groupedItems.map(item => item.split('-')[1]))
      .value();

    if (searchTerm) {
      const terms = searchTerm.split(' ').map((t) => t.replace(/\(\)-\*/g, ''))
      filteredRows = fuse.search(terms.join("|")).map((r) => r.item)
    }

    if (selectedFilterIds.length && filters) {
      Object.keys(activeFilters).forEach((activeFilterTitle: string) => {
        const filter = filters.find((filter) => filter.title === activeFilterTitle)
        if (!filter) {
          console.warn(`Filter not found by title ${activeFilterTitle}`)
          return
        }

        filteredRows = filteredRows.filter((row: T) => (
          (() => {
            const value = filter.matchingFunction(row)
            if (Array.isArray(value)) {
              return value.some((v) => activeFilters[activeFilterTitle].includes(v))
            }
            return activeFilters[activeFilterTitle].includes(value)
          })()
        ))
      })
    }

    filteredRows.sort(sort)
    setDisplayedRows(filteredRows)
  }, [rows, filtersOn, selectedFilterIds, searchTerm, filters, sortState, fuse, sort])

  const csvColumns = columns.filter((c) => c.csv)
  const csvData = React.useMemo(() => (csvColumns
    ? displayedRows.map((row) => (csvColumns.map((c) => {
      if (c.csvFill) {
        return c.csvFill(row)
      }

      if (typeof c.cellFill === 'function') {
        return c.cellFill(row)
      }

      return row[c.cellFill]
    }
    )))
    : []), [csvColumns, displayedRows])


  const handleSearchChange = (newValue: string) => {
    setStoreState(id, { searchTerm: newValue, indexPageNumber: 0 })
  }

  const styles = useStyles()

  const handleHeaderClicked = (selectedId: keyof T) => {
    const isSameKey = selectedId === sortState.key
    let ascending = true
    if (isSameKey && sortState.key && sortState.ascending) {
      ascending = false
    }
    const newState: SortState<T> = {
      ascending,
      key: selectedId
    }
    setStoreState(id, { sortState: newState });
  }

  interface HeaderCellProps {
    id: string,
    keyProp?: React.Key
  }

  const HeaderCell = ({ id, children, keyProp }: PropsWithChildren<HeaderCellProps>) => {
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const cellSelected = sortState.key && sortState.key === id;
    const showIcon = isHovered || cellSelected;
    const opacity = cellSelected ? 1 : 0.2
    let Icon = ArrowUp;
    if (cellSelected && !sortState.ascending) {
      Icon = ArrowDown
    }
    return (
      <TableCell key={keyProp} onMouseOver={() => setIsHovered(true)} onMouseOut={() => setIsHovered(false)} style={{ cursor: 'pointer' }} onClick={() => handleHeaderClicked(id as keyof T)} classes={{ root: styles.cell, head: styles.headCell }}>
        <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          {children}<Box style={{ width: 25, height: 25, marginLeft: 4 }}>{showIcon ? <Icon size={22} opacity={opacity} /> : null}</Box>
        </Box>
      </TableCell>
    )
  }

  const MainTable = () => {
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      const rows = parseInt(event.target.value, 10);
      setStoreState(id, { indexPageNumber: 0, indexRowsPerPage: rows })
    };

    return (
      <>
        <TableContainer className={styles.tableContainer}>
          <MuiTable stickyHeader size="small" classes={{ root: styles.tableRoot }}>
            <TableHead>
              <TableRow>
                {columns.map((column, i) => {
                  if (column.hide) {
                    return null
                  }
                  return (
                    <HeaderCell id={column.label} key={i} keyProp={i}><strong>{column.label}</strong></HeaderCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRows.slice(indexPageNumber * indexRowsPerPage, indexPageNumber * indexRowsPerPage + indexRowsPerPage).map((row: T, i: number) => {
                return (
                  <TableRow hover className={styles.row} key={i} onClick={() => handleRowClicked(row)}>
                    {columns.map((column, i) => {
                      if (column.hide) {
                        return null
                      }
                      return (
                        <TableCell key={i} classes={{ root: styles.cell }}>{typeof column.cellFill === 'function' ? column.cellFill(row) : row[column.cellFill]}</TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>
        <TablePagination
          style={{ overflow: 'hidden' }}
          component="div"
          count={displayedRows.length}
          page={indexPageNumber}
          rowsPerPage={indexRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, newPageIndex: number) => setStoreState(id, { indexPageNumber: newPageIndex })}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </>
    )
  }

  const handleSelectGroup = (groupTitle: string, values: string[]) => {
    // Replace selections for a specific group (identified by `${title}-...`)
    const remaining = selectedFilterIds.filter((filterId) => !filterId.startsWith(`${groupTitle}-`));
    setSelectedFilters([...remaining, ...values]);
  }

  const removeChip = (id: string) => {
    setSelectedFilters(selectedFilterIds.filter((filterId) => filterId !== id));
  }

  const Filters: FC = () => {
    if (!filters) {
      return null
    }

    return (
      <Box className={styles.filtersContainer}>
        {filterOptionGroups.map((fog: FilterOptionGroup, i: number) => {
          const groupValue = selectedFilterIds.filter((id) => id.startsWith(`${fog.title}-`));
          const labelId = `filter-${fog.title}-label`;
          const handleChange = (event: SelectChangeEvent<string[]>, _child?: ReactNode) => {
            const rawValue = event.target.value;
            const valueArray = Array.isArray(rawValue) ? rawValue : String(rawValue).split(',');
            handleSelectGroup(fog.title, valueArray);
          }

          return (
            <Box className={styles.filterContainer} key={i}>
              <FormControl size="small" className={styles.filterSelect}>
                <InputLabel id={labelId}>{fog.title}</InputLabel>
                <Select
                  labelId={labelId}
                  label={fog.title}
                  multiple
                  value={groupValue}
                  onChange={handleChange}
                  renderValue={() => fog.title}
                >
                  {fog.options.map((o, i) => {
                    const selected = selectedFilterIds.includes(o.id)
                    return (
                      <MenuItem key={i} value={o.id} className={styles.filterMenuItem}>
                        <Checkbox size="small" checked={selected} />
                        <ListItemText primary={o.displayLabel} classes={{ primary: styles.filterLabel }} />
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            </Box >
          )
        })
        }
      </Box >
    )
  }

  const SelectedChips: FC = () => {
    if (!filters || selectedFilterIds.length === 0) {
      return null;
    }

    return (
      <Box className={styles.chipContainer}>
        {selectedFilterIds.map((id) => (
          <Chip
            key={id}
            label={id.split('-')[1]}
            size="small"
            className={styles.chip}
            onDelete={() => removeChip(id)}
          />
        ))}
      </Box>
    )
  }

  return (
    <Box className={styles.container}>
      <Box mb={2} className={styles.controlsContainer}>
        <Box mr={2} className={styles.searchContainer}>
          <SearchBar fullWidth value={searchTerm} onChange={handleSearchChange} />
        </Box>
        <Box>
          {(filters && filters.length > 0 ? (
            <Badge badgeContent={selectedFilterIds.length} color="primary" invisible={selectedFilterIds.length === 0}>
              <Button variant="text" startIcon={<FilterIcon />} onClick={() => toggleFiltersOn(!filtersOn)}>
                Filters
              </Button>
            </Badge>
          ) : null)}
        </Box>
        {rightActions ? (
          <Box ml={1} padding={3} display="flex" alignItems="center">
            {rightActions}
          </Box>
        ) : null}
      </Box>
      <Box className={styles.filtersSection}>
        <Collapse in={filtersOn}>
          <Filters />
          <SelectedChips />
        </Collapse>
      </Box>
      <Box className={styles.exportContainer}>
        <Box mr={2}>
          <h3>{displayedRows.length} {title}</h3>
        </Box>
        <Button variant="text">
          <CSVLink data={csvData} filename={"Stitch.csv"} headers={csvColumns.map((c) => c.label)}>
            Export CSV
          </CSVLink>
        </Button>
      </Box>
      <MainTable />
    </Box>
  )
}

const useStyles = makeStyles({
  exportContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 8,
  },
  checksContainer: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 250,
    flexWrap: 'wrap',
  },
  container: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    minHeight: 0,
  },
  tableContainer: {
    flex: '1 1 auto',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 220px)',
  },
  tableRoot: {
    '& th, & td': {
      padding: '8px 10px',
      fontSize: 13,
      lineHeight: 1.3,
    },
  },
  nameContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  leftMenuContainer: {
    width: 300,
    height: '100%',
    borderRight: `2px solid ${colors.GREY_6}`
  },
  closeButton: {
    width: 45,
    height: 45,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: "100%",
    background: colors.GREY_2,
    cursor: 'pointer',
  },
  closeContainer: {
    position: 'relative',
    background: colors.STITCH_BLUE,
    color: colors.WHITE,
    display: 'flex',
    borderTopLeftRadius: 20,
  },
  controlsContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
  },
  searchContainer: {
    width: '32%',
    flex: '0 1 auto',
    '& input': {
      padding: '10px 12px',
      fontSize: 13,
    },
  },
  boldText: {
    fontWeight: 'bold',
  },
  row: {
    cursor: 'pointer',
    height: 44,
  },
  mrr: {
    color: colors.DARK_GREEN,
    fontWeight: 'bold',
  },
  filtersContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 12,
    padding: '8px 0',
  },
  filtersSection: {
    marginBottom: 16,
    minHeight: 12, // ensures space remains even if Collapse animates
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: 16,
    minWidth: 150,
  },
  filterSelect: {
    minWidth: 150,
    '& .MuiInputBase-root': {
      fontSize: 13,
    },
    '& .MuiSelect-select': {
      padding: '6px 10px',
    },
    '& .MuiInputLabel-root': {
      fontSize: 12,
    },
  },
  filterLabel: {
    fontSize: 13,
  },
  filterMenuItem: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    paddingTop: 2,
  },
  chip: {
    height: 22,
    fontSize: 12,
  },
  cell: {},
  headCell: {
    fontWeight: 700,
  }
})

export default Table
